import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  UseQueryResult,
  useMutation,
  MutationKey,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";

import { AxiosError, Method } from "axios";
import { axiosInstance } from "./axiosConfig";

export type ApiMethodType = Extract<
  Method,
  "GET" | "PUT" | "DELETE" | "POST" | "PATCH"
>;

type TokenType = string;
export type TokenSourceFnType = () => TokenType | null;
export type ResourcePathType<Params> = string | ((params: Params) => string);

type GetQueryOptions<Params, ResponseType> = {
  key: QueryKey;
  params?: Params;
  options?: UseQueryOptions<ResponseType, AxiosError, ResponseType, QueryKey>;
};

type UpdateApiHookOptions = {
  isFormDataMethod: boolean;
};

export type UpdateHookOptions<PayloadType, Params, ResponseType> = {
  key: MutationKey;
  params?: Params;
  options?: UseMutationOptions<ResponseType, AxiosError, PayloadType>;
};

export type MutationResult<PayloadType, ResponseType> = Omit<
  UseMutationResult<
    ResponseType,
    AxiosError<unknown, any>,
    PayloadType,
    unknown
  >,
  "mutateAsync"
> & {
  update: UseMutationResult<
    ResponseType,
    AxiosError,
    PayloadType
  >["mutateAsync"];
};

const DEFAULT_QUERY_OPTIONS = {
  refetchOnMount: true,
  retry: false,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} satisfies UseQueryOptions;

export class HookCreator {
  private method?: ApiMethodType;

  private tokenSrcFn: TokenSourceFnType;

  constructor(tokenSrcFn: TokenSourceFnType);

  constructor(method: ApiMethodType, tokenSrcFn: TokenSourceFnType);

  constructor(
    methodOrTokenSrcFn: ApiMethodType | TokenSourceFnType,
    tokenSrcFn?: TokenSourceFnType
  ) {
    if (methodOrTokenSrcFn instanceof Function) {
      this.tokenSrcFn = methodOrTokenSrcFn;
    } else {
      this.method = methodOrTokenSrcFn;
      this.tokenSrcFn = tokenSrcFn!;
    }
    this.getResolverPath = this.getResolverPath.bind(this);
    this.createGetApiHookCreator = this.createGetApiHookCreator.bind(this);
    this.createUpdateApiHookCreator =
      this.createUpdateApiHookCreator.bind(this);
  }

  private getResolverPath<Params>(
    resourcePathOrPathResolverFn: ResourcePathType<Params>,
    params: Params
  ): string {
    return typeof resourcePathOrPathResolverFn === "string"
      ? resourcePathOrPathResolverFn
      : resourcePathOrPathResolverFn(params as Params);
  }

  private async fetcherFunction<ResponseType>(
    url: string,
    extraHeaders: Record<string, string> = {}
  ): Promise<ResponseType> {
    const token = this.tokenSrcFn();
    const response = await axiosInstance({
      url,
      headers: {
        Authorization: token ? `Bearer ${token}` : null,
        ...extraHeaders,
      },
    });
    return await response.data;
  }

  private async mutatorFunction<PayloadType extends object, ResponseType>(
    url: string,
    data: PayloadType,
    isFormDataMethod: boolean = true,
    extraHeaders: Record<string, string> = {} // NEW
  ): Promise<ResponseType> {
    try {
      const apiResponse = await axiosInstance({
        url,
        method: this.method,
        headers: {
          Accept: "application/json",
          "Content-Type": isFormDataMethod
            ? "multipart/formdata"
            : "application/json",
          Authorization: `Bearer ${this.tokenSrcFn()}`,
          ...extraHeaders, // NEW
        },
        data,
      });
      return apiResponse.data;
    } catch (error: any) {
      throw error;
    }
  }

  public createGetApiHookCreator<ResponseType, Params extends object = {}>(
    resourcePathOrPathResolverFn: ResourcePathType<Params>
  ): (
    options: GetQueryOptions<Params, ResponseType>
  ) => UseQueryResult<ResponseType, AxiosError<unknown, any>> {
    return ({
      key,
      params,
      options = {},
    }: GetQueryOptions<Params, ResponseType>): UseQueryResult<
      ResponseType,
      AxiosError<unknown, any>
    > => {
      const resourcePath = this.getResolverPath(
        resourcePathOrPathResolverFn,
        params as Params
      );

      const queryResponse = useQuery<
        ResponseType,
        AxiosError,
        ResponseType,
        QueryKey
      >({
        queryKey: key,
        queryFn: () =>
          this.fetcherFunction(resourcePath, (params as any)?.headers),
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
      });

      return queryResponse;
    };
  }

  public createUpdateApiHookCreator<
    PayloadType extends object = {},
    Params = {},
    ResponseType = {},
  >(
    resourcePathOrPathResolverFn: ResourcePathType<Params>,
    { isFormDataMethod = true }: UpdateApiHookOptions
  ): (
    options: UpdateHookOptions<PayloadType, Params, ResponseType>
  ) => MutationResult<PayloadType, ResponseType> {
    return ({
      params,
      key,
      options = {},
    }: UpdateHookOptions<PayloadType, Params, ResponseType>): MutationResult<
      PayloadType,
      ResponseType
    > => {
      const headers = (params as any)?.headers ?? {};
      const urlParams = { ...params };

      const resourcePath = this.getResolverPath(
        resourcePathOrPathResolverFn,
        urlParams as Params
      );

      const queryResponse = useMutation<ResponseType, AxiosError, PayloadType>(
        key,
        (data: PayloadType) =>
          this.mutatorFunction(resourcePath, data, isFormDataMethod, headers),
        { ...options }
      );

      const response = removeRecord(queryResponse, ["mutateAsync"]);

      return {
        ...response,
        update: queryResponse.mutateAsync,
      };
    };
  }
}

export function removeRecord<T, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const newObject = Object.assign({}, obj);

  for (let key of keys) {
    delete newObject[key];
  }

  return newObject as Omit<T, K>;
}
